<template>
  <div class="notificacoes-container">
    <!-- Header -->
    <div class="page-header">
      <h1>Notificações</h1>
      <p class="page-description">
        Gerencie templates de mensagens e campanhas de envio para seus pacientes.
      </p>
    </div>

    <!-- Tabs -->
    <div class="tabs-container">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="tab-btn"
        :class="{ active: abaAtiva === tab.id }"
        @click="abaAtiva = tab.id"
      >
        <i :class="tab.icon"></i>
        {{ tab.label }}
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- Instruções -->
      <div v-if="abaAtiva === 'instrucoes'" class="tab-instrucoes">
        <div class="instrucoes-content">
          <h2>Como funciona o sistema de notificações</h2>
          <p class="instrucoes-intro">
            Sistema completo para envio de <strong>Email</strong> e <strong>WhatsApp</strong> para seus pacientes,
            com suporte a campanhas manuais, agendadas e lembretes automáticos.
          </p>

          <div class="instrucoes-grid">
            <!-- Passo 1 -->
            <div class="instrucao-card">
              <div class="instrucao-numero">1</div>
              <h3>Configure os Webhooks</h3>
              <p>
                Na aba <strong>Webhooks</strong>, configure a URL e o token de autenticação
                do seu serviço de automação (n8n, Make, Zapier, etc.).
              </p>
            </div>

            <!-- Passo 2 -->
            <div class="instrucao-card">
              <div class="instrucao-numero">2</div>
              <h3>Crie Templates</h3>
              <p>
                Na aba <strong>Templates</strong>, crie modelos de mensagem com variáveis personalizadas.
                Ative o modo IA para gerar conteúdo dinâmico automaticamente.
              </p>
            </div>

            <!-- Passo 3 -->
            <div class="instrucao-card">
              <div class="instrucao-numero">3</div>
              <h3>Crie Campanhas</h3>
              <p>
                Na aba <strong>Campanhas</strong>, selecione um template, aplique filtros de destinatários
                e envie imediatamente ou agende para uma data futura.
              </p>
            </div>

            <!-- Passo 4 -->
            <div class="instrucao-card">
              <div class="instrucao-numero">4</div>
              <h3>Configure Lembretes</h3>
              <p>
                Na aba <strong>Lembretes</strong>, crie automações para aniversários, boas-vindas,
                reengajamento de inativos ou envios recorrentes.
              </p>
            </div>
          </div>

          <div class="instrucoes-section">
            <h3>Tipos de Envio</h3>
            <div class="tipos-envio-grid">
              <div class="tipo-envio-item">
                <span class="tipo-icon">📤</span>
                <strong>Campanhas Manuais</strong>
                <p>Envio imediato para lista filtrada de pacientes</p>
              </div>
              <div class="tipo-envio-item">
                <span class="tipo-icon">📅</span>
                <strong>Campanhas Agendadas</strong>
                <p>Programar envio para data e hora específicas</p>
              </div>
              <div class="tipo-envio-item">
                <span class="tipo-icon">🎂</span>
                <strong>Lembrete Aniversário</strong>
                <p>Envio automático no dia do aniversário</p>
              </div>
              <div class="tipo-envio-item">
                <span class="tipo-icon">👋</span>
                <strong>Lembrete Boas-vindas</strong>
                <p>Envio X dias após o cadastro do paciente</p>
              </div>
              <div class="tipo-envio-item">
                <span class="tipo-icon">🔄</span>
                <strong>Lembrete Inatividade</strong>
                <p>Reengajamento de pacientes sem interação</p>
              </div>
              <div class="tipo-envio-item">
                <span class="tipo-icon">📆</span>
                <strong>Lembrete Recorrente</strong>
                <p>Envio diário, semanal ou mensal</p>
              </div>
            </div>
          </div>

          <div class="instrucoes-section">
            <h3>Variáveis disponíveis nos templates</h3>
            <div class="variaveis-grid">
              <div class="variavel-item"><code>{primeiro_nome}</code> <span>Nome do paciente</span></div>
              <div class="variavel-item"><code>{sobrenome}</code> <span>Sobrenome</span></div>
              <div class="variavel-item"><code>{email}</code> <span>E-mail</span></div>
              <div class="variavel-item"><code>{telefone_formatado}</code> <span>Telefone</span></div>
              <div class="variavel-item"><code>{idade}</code> <span>Idade</span></div>
              <div class="variavel-item"><code>{sexo}</code> <span>Sexo (M/F/O)</span></div>
              <div class="variavel-item"><code>{uf}</code> <span>Estado (UF)</span></div>
              <div class="variavel-item"><code>{sistema_inscrito}</code> <span>Sistema inscrito</span></div>
              <div class="variavel-item"><code>{indicacao_clinica}</code> <span>Indicação clínica</span></div>
              <div class="variavel-item"><code>{data_cadastro}</code> <span>Data de cadastro</span></div>
            </div>
          </div>

          <div class="instrucoes-section">
            <h3>Filtros de Destinatários</h3>
            <p>
              Segmente seus envios por <strong>Estado (UF)</strong>, <strong>Sistema inscrito</strong>,
              <strong>Sexo</strong> e <strong>Faixa etária</strong>. Apenas pacientes que aceitaram
              receber comunicações pelo canal selecionado serão incluídos.
            </p>
          </div>

          <div class="instrucoes-section">
            <h3>Fluxo de envio</h3>
            <div class="fluxo-visual">
              <div class="fluxo-item">Sistema</div>
              <div class="fluxo-seta">→</div>
              <div class="fluxo-item">Webhook</div>
              <div class="fluxo-seta">→</div>
              <div class="fluxo-item">Automação</div>
              <div class="fluxo-seta">→</div>
              <div class="fluxo-item destaque-email">Email</div>
              <div class="fluxo-ou">ou</div>
              <div class="fluxo-item destaque-whatsapp">WhatsApp</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Templates -->
      <TemplateList
        v-if="abaAtiva === 'templates'"
        :templates="templatesStore.templates.value"
        :loading="templatesStore.loading.value"
        @criar="templatesStore.abrirModalCriar()"
        @editar="templatesStore.abrirModalEditar($event)"
        @excluir="handleExcluirTemplate"
        @preview="handlePreviewTemplate"
        @carregar="handleCarregarTemplates"
      />

      <!-- Campanhas -->
      <CampaignList
        v-if="abaAtiva === 'campanhas'"
        :campanhas="campanhasStore.campanhas.value"
        :paginacao="campanhasStore.paginacao.value"
        :loading="campanhasStore.loading.value"
        @criar="campanhasStore.abrirModalCriar()"
        @editar="campanhasStore.abrirModalEditar($event)"
        @excluir="handleExcluirCampanha"
        @enviar="handleEnviarCampanha"
        @reenviar="handleReenviarCampanha"
        @cancelar="handleCancelarCampanha"
        @duplicar="handleDuplicarCampanha"
        @detalhes="handleDetalhesCampanha"
        @carregar="handleCarregarCampanhas"
      />

      <!-- Lembretes -->
      <ReminderList
        v-if="abaAtiva === 'lembretes'"
        :lembretes="lembretesStore.lembretes.value"
        :loading="lembretesStore.loading.value"
        @criar="lembretesStore.abrirModalCriar()"
        @editar="lembretesStore.abrirModalEditar($event)"
        @excluir="handleExcluirLembrete"
        @toggle="handleToggleLembrete"
        @logs="handleLogsLembrete"
        @carregar="handleCarregarLembretes"
      />

      <!-- Mídias -->
      <MediaLibrary v-if="abaAtiva === 'midias'" />

      <!-- Histórico -->
      <NotificationLogs
        v-if="abaAtiva === 'historico'"
        :logs="logsStore.logs.value"
        :stats="logsStore.stats.value"
        :paginacao="logsStore.paginacao.value"
        :loading="logsStore.loading.value"
        @carregar="handleCarregarLogs"
        @carregar-stats="handleCarregarStats"
      />

      <!-- Webhooks -->
      <div v-if="abaAtiva === 'webhooks'" class="tab-webhooks">
        <div class="webhooks-content">
          <div class="webhooks-header">
            <h2>Configuração de Webhooks</h2>
            <p>Configure a URL e o token de autenticação para integração com seu serviço de automação (n8n, Make, Zapier, etc.).</p>
          </div>

          <div class="webhooks-form">
            <div class="form-group">
              <label for="webhook-url">URL Base do Webhook</label>
              <input
                id="webhook-url"
                v-model="webhookForm.n8n_webhook_url"
                type="url"
                class="form-input"
                placeholder="https://seu-servidor.com/webhook"
                autocomplete="off"
              />
              <span class="form-hint">URL base do seu endpoint. O sistema adiciona <code>/campanha-email</code> e <code>/campanha-whatsapp</code> automaticamente.</span>
            </div>

            <div class="form-group">
              <label for="webhook-token">Token de Autenticação</label>
              <div class="token-field">
                <div class="input-with-toggle">
                  <input
                    id="webhook-token"
                    v-model="webhookForm.n8n_webhook_token"
                    :type="mostrarToken ? 'text' : 'password'"
                    class="form-input"
                    placeholder="Clique em 'Gerar Token' para criar um token seguro"
                    autocomplete="off"
                    readonly
                  />
                  <button class="btn-toggle-token" type="button" @click="mostrarToken = !mostrarToken">
                    {{ mostrarToken ? '🙈' : '👁️' }}
                  </button>
                </div>
                <div class="token-actions">
                  <button class="btn-gerar-token" type="button" @click="gerarToken">
                    Gerar Token
                  </button>
                  <button
                    class="btn-copiar-token"
                    type="button"
                    :disabled="!webhookForm.n8n_webhook_token"
                    @click="copiarToken"
                  >
                    Copiar
                  </button>
                </div>
              </div>
              <span class="form-hint">Token Bearer enviado no header <code>Authorization</code> para autenticar as requisições.</span>
            </div>

            <div class="form-group">
              <label for="whatsapp-instance">Instância/Sessão do WhatsApp</label>
              <input
                id="whatsapp-instance"
                v-model="webhookForm.whatsapp_instance"
                type="text"
                class="form-input"
                placeholder="nome-da-instancia"
                autocomplete="off"
              />
              <span class="form-hint">Nome da instância/sessão do WhatsApp. Enviado no header <code>X-WhatsApp-Instance</code> em todas as requisições.</span>
            </div>

            <div class="webhooks-actions">
              <button
                class="btn-salvar-webhook"
                :disabled="salvandoWebhook"
                @click="salvarWebhookSettings"
              >
                {{ salvandoWebhook ? 'Salvando...' : 'Salvar Configurações' }}
              </button>

              <button
                class="btn-testar-webhook"
                :disabled="testandoWebhook || !webhookForm.n8n_webhook_url"
                @click="testarWebhookConexao"
              >
                {{ testandoWebhook ? 'Testando...' : 'Testar Conexão' }}
              </button>
            </div>

            <!-- Status do teste -->
            <div v-if="webhookTestResult" class="webhook-test-result" :class="webhookTestResult.success ? 'success' : 'error'">
              <div class="test-main">
                <span class="test-icon">{{ webhookTestResult.success ? '✅' : '❌' }}</span>
                <span>{{ webhookTestResult.message }}</span>
              </div>
              <div v-if="webhookTestResult.detalhes && webhookTestResult.success" class="test-detalhes">
                <span class="canal-ok">Email: {{ webhookTestResult.detalhes.email?.url }}</span>
                <span class="canal-ok">WhatsApp: {{ webhookTestResult.detalhes.whatsapp?.url }}</span>
              </div>
            </div>
          </div>

          <div class="webhooks-info">
            <h3>Endpoints Disponíveis</h3>
            <p>O sistema envia requisições POST para os seguintes endpoints:</p>
            <div class="workflow-list">
              <div class="workflow-item">
                <span class="workflow-icon">📧</span>
                <div>
                  <strong>/campanha-email</strong>
                  <p>Recebe dados de campanhas e lembretes para envio via Email</p>
                </div>
              </div>
              <div class="workflow-item">
                <span class="workflow-icon">💬</span>
                <div>
                  <strong>/campanha-whatsapp</strong>
                  <p>Recebe dados de campanhas e lembretes para envio via WhatsApp</p>
                </div>
              </div>
            </div>

            <h3>Integrações Recomendadas</h3>
            <div class="credenciais-list">
              <div class="credencial-item">
                <span class="credencial-badge email">Email</span>
                <span>Gmail, SMTP, SendGrid, Mailgun, Amazon SES</span>
              </div>
              <div class="credencial-item">
                <span class="credencial-badge whatsapp">WhatsApp</span>
                <span>Evolution API, WhatsApp Business API, Twilio</span>
              </div>
              <div class="credencial-item">
                <span class="credencial-badge ia">IA (opcional)</span>
                <span>OpenAI, Groq, Anthropic para geração de conteúdo dinâmico</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Template Modal -->
    <TemplateModal
      :mostrar="templatesStore.mostrarModal.value"
      :modo-edicao="templatesStore.modoEdicao.value"
      :template="templatesStore.templateSelecionado.value"
      :variaveis-disponiveis="templatesStore.variaveisDisponiveis.value"
      :loading="templatesStore.loading.value"
      @fechar="templatesStore.fecharModal()"
      @salvar="handleSalvarTemplate"
    />

    <!-- Campaign Modal -->
    <CampaignModal
      :mostrar="campanhasStore.mostrarModal.value"
      :modo-edicao="campanhasStore.modoEdicao.value"
      :campanha="campanhasStore.campanhaSelecionada.value"
      :templates="templatesStore.templatesAtivos.value"
      :loading="campanhasStore.loading.value"
      @fechar="campanhasStore.fecharModal()"
      @salvar="handleSalvarCampanha"
      @contar-destinatarios="handleContarDestinatarios"
    />

    <!-- Reminder Modal -->
    <ReminderModal
      :mostrar="lembretesStore.mostrarModal.value"
      :modo-edicao="lembretesStore.modoEdicao.value"
      :lembrete="lembretesStore.lembreteSelecionado.value"
      :templates="templatesStore.templatesAtivos.value"
      :loading="lembretesStore.loading.value"
      @fechar="lembretesStore.fecharModal()"
      @salvar="handleSalvarLembrete"
      @contar-destinatarios="handleContarDestinatariosLembrete"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import Swal from 'sweetalert2'
import axios from 'axios'

// Components
import TemplateList from '@/components/notificacoes/TemplateList.vue'
import TemplateModal from '@/components/notificacoes/TemplateModal.vue'
import CampaignList from '@/components/notificacoes/CampaignList.vue'
import CampaignModal from '@/components/notificacoes/CampaignModal.vue'
import NotificationLogs from '@/components/notificacoes/NotificationLogs.vue'
import MediaLibrary from '@/components/notificacoes/MediaLibrary.vue'
import ReminderList from '@/components/notificacoes/ReminderList.vue'
import ReminderModal from '@/components/notificacoes/ReminderModal.vue'

// Composables
import { useNotificationTemplates } from '@/composables/useNotificationTemplates'
import { useNotificationCampaigns } from '@/composables/useNotificationCampaigns'
import { useNotificationLogs } from '@/composables/useNotificationLogs'
import { useNotificationReminders } from '@/composables/useNotificationReminders'

const templatesStore = useNotificationTemplates()
const campanhasStore = useNotificationCampaigns()
const logsStore = useNotificationLogs()
const lembretesStore = useNotificationReminders()

const abaAtiva = ref('instrucoes')

const tabs = [
  { id: 'instrucoes', label: 'Instruções', icon: 'fas fa-info-circle' },
  { id: 'templates', label: 'Templates', icon: 'fas fa-file-alt' },
  { id: 'campanhas', label: 'Campanhas', icon: 'fas fa-paper-plane' },
  { id: 'lembretes', label: 'Lembretes', icon: 'fas fa-bell' },
  { id: 'midias', label: 'Mídias', icon: 'fas fa-photo-video' },
  { id: 'historico', label: 'Histórico', icon: 'fas fa-history' },
  { id: 'webhooks', label: 'Webhooks', icon: 'fas fa-plug' },
]

// === WEBHOOK SETTINGS ===
const webhookForm = ref({
  n8n_webhook_url: '',
  n8n_webhook_token: '',
  whatsapp_instance: '',
})
const mostrarToken = ref(false)
const salvandoWebhook = ref(false)
const testandoWebhook = ref(false)
const webhookTestResult = ref(null)

function gerarToken() {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  const token = Array.from(array, b => b.toString(16).padStart(2, '0')).join('')
  webhookForm.value.n8n_webhook_token = token
  mostrarToken.value = true
}

function copiarToken() {
  navigator.clipboard.writeText(webhookForm.value.n8n_webhook_token).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Copiado!',
      text: 'Token copiado para a área de transferência.',
      timer: 1500,
      showConfirmButton: false,
    })
  })
}

async function carregarWebhookSettings() {
  try {
    const { data } = await axios.get('/api/notificacoes/settings')
    webhookForm.value.n8n_webhook_url = data.settings.n8n_webhook_url || ''
    webhookForm.value.n8n_webhook_token = data.settings.n8n_webhook_token || ''
    webhookForm.value.whatsapp_instance = data.settings.whatsapp_instance || ''
  } catch {
    // Sem settings ainda — mantém vazio
  }
}

async function salvarWebhookSettings() {
  salvandoWebhook.value = true
  webhookTestResult.value = null
  try {
    await axios.post('/api/notificacoes/settings', webhookForm.value)
    Swal.fire('Salvo!', 'Configurações do webhook salvas com sucesso.', 'success')
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao salvar configurações.'
      Swal.fire('Erro!', msg, 'error')
    }
  } finally {
    salvandoWebhook.value = false
  }
}

async function testarWebhookConexao() {
  testandoWebhook.value = true
  webhookTestResult.value = null
  try {
    // Salva antes de testar
    await axios.post('/api/notificacoes/settings', webhookForm.value)
    const { data } = await axios.post('/api/notificacoes/settings/testar')
    webhookTestResult.value = data
  } catch (err) {
    webhookTestResult.value = {
      success: false,
      message: err.response?.data?.message || 'Falha ao testar conexão com n8n.',
    }
  } finally {
    testandoWebhook.value = false
  }
}

// === TEMPLATES ===
async function handleCarregarTemplates(filtros = {}) {
  try {
    await templatesStore.carregarTemplates(filtros)
  } catch (err) {
    if (!err._handled) {
      console.error('Erro ao carregar templates:', err)
    }
  }
}

async function handleSalvarTemplate(dados) {
  try {
    if (templatesStore.modoEdicao.value) {
      await templatesStore.atualizarTemplate(templatesStore.templateSelecionado.value.id, dados)
      Swal.fire('Sucesso!', 'Template atualizado com sucesso.', 'success')
    } else {
      await templatesStore.criarTemplate(dados)
      Swal.fire('Sucesso!', 'Template criado com sucesso.', 'success')
    }
    templatesStore.fecharModal()
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao salvar template.'
      Swal.fire('Erro!', msg, 'error')
    }
  }
}

async function handleExcluirTemplate(template) {
  try {
    await templatesStore.excluirTemplate(template.id)
    Swal.fire('Excluído!', 'Template excluído com sucesso.', 'success')
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao excluir template.'
      Swal.fire('Erro!', msg, 'error')
    }
  }
}

async function handlePreviewTemplate(template) {
  try {
    const data = await templatesStore.previewTemplate(template.id)
    Swal.fire({
      title: template.nome,
      html: `
        ${data.preview.assunto ? `<p><strong>Assunto:</strong> ${data.preview.assunto}</p>` : ''}
        <div style="text-align: left; background: #f5f5f5; padding: 15px; border-radius: 8px; white-space: pre-wrap;">${data.preview.corpo}</div>
        <p style="margin-top: 15px; font-size: 12px; color: #888;">
          Exemplo com: ${data.preview.paciente_exemplo.primeiro_nome} (${data.preview.paciente_exemplo.email})
        </p>
      `,
      width: 600,
      confirmButtonText: 'Fechar',
    })
  } catch (err) {
    if (!err._handled) {
      Swal.fire('Erro!', 'Não foi possível gerar o preview.', 'error')
    }
  }
}

// === CAMPANHAS ===
async function handleCarregarCampanhas(filtros = {}, pagina = 1) {
  try {
    await campanhasStore.carregarCampanhas(filtros, pagina)
  } catch (err) {
    if (!err._handled) {
      console.error('Erro ao carregar campanhas:', err)
    }
  }
}

async function handleSalvarCampanha(dados) {
  try {
    if (campanhasStore.modoEdicao.value) {
      await campanhasStore.atualizarCampanha(campanhasStore.campanhaSelecionada.value.id, dados)
      Swal.fire('Sucesso!', 'Campanha atualizada com sucesso.', 'success')
    } else {
      await campanhasStore.criarCampanha(dados)
      Swal.fire('Sucesso!', 'Campanha criada com sucesso.', 'success')
    }
    campanhasStore.fecharModal()
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao salvar campanha.'
      Swal.fire('Erro!', msg, 'error')
    }
  }
}

async function handleExcluirCampanha(campanha) {
  try {
    await campanhasStore.excluirCampanha(campanha.id)
    Swal.fire('Excluída!', 'Campanha excluída com sucesso.', 'success')
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao excluir campanha.'
      Swal.fire('Erro!', msg, 'error')
    }
  }
}

// === POLLING CAMPANHA EM ANDAMENTO ===
let pollingInterval = null

function pararPolling() {
  if (pollingInterval) {
    clearInterval(pollingInterval)
    pollingInterval = null
  }
}

function iniciarPolling(campanhaId) {
  pararPolling()
  pollingInterval = setInterval(async () => {
    try {
      const { data } = await axios.get(`/api/notificacoes/campanhas/${campanhaId}/status`)

      // Atualizar campanha na lista local
      const idx = campanhasStore.campanhas.value.findIndex(c => c.id === campanhaId)
      if (idx >= 0) {
        campanhasStore.campanhas.value[idx] = {
          ...campanhasStore.campanhas.value[idx],
          ...data,
        }
      }

      // Se terminou, parar polling e atualizar lista
      if (['concluido', 'erro', 'cancelado'].includes(data.status)) {
        pararPolling()
        await handleCarregarCampanhas()
        Swal.fire({
          icon: data.status === 'concluido' ? 'success' : 'warning',
          title: data.status === 'concluido' ? 'Campanha concluída!' : 'Campanha finalizada',
          html: `
            <p>Enviados: <strong style="color:green;">${data.total_enviados}</strong></p>
            <p>Falhas: <strong style="color:red;">${data.total_falhas}</strong></p>
          `,
          timer: 5000,
        })
      }
    } catch {
      // Erro silencioso no polling
    }
  }, 5000) // Polling a cada 5 segundos
}

async function handleEnviarCampanha(campanha) {
  const result = await Swal.fire({
    title: 'Enviar campanha?',
    html: `
      <p>A campanha <strong>${campanha.nome}</strong> será enviada para <strong>${campanha.total_destinatarios}</strong> destinatários.</p>
      <p style="color: #888; font-size: 13px;">Esta ação não pode ser desfeita.</p>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#4caf50',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Sim, enviar agora',
    cancelButtonText: 'Cancelar',
  })

  if (!result.isConfirmed) return

  try {
    await campanhasStore.enviarCampanha(campanha.id)
    Swal.fire('Enviando!', 'A campanha está sendo processada. O progresso será atualizado automaticamente.', 'success')
    iniciarPolling(campanha.id)
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao enviar campanha.'
      Swal.fire('Erro!', msg, 'error')
    }
  }
}

async function handleReenviarCampanha(campanha) {
  try {
    await campanhasStore.enviarCampanha(campanha.id)
    Swal.fire('Reenviando!', 'A campanha está sendo reprocessada.', 'success')
    iniciarPolling(campanha.id)
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao reenviar campanha.'
      Swal.fire('Erro!', msg, 'error')
    }
  }
}

async function handleCancelarCampanha(campanha) {
  try {
    await campanhasStore.cancelarCampanha(campanha.id)
    Swal.fire('Cancelada!', 'A campanha foi cancelada.', 'info')
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao cancelar campanha.'
      Swal.fire('Erro!', msg, 'error')
    }
  }
}

async function handleDuplicarCampanha(campanha) {
  try {
    const dados = {
      nome: `${campanha.nome} (cópia)`,
      template_id: campanha.template_id,
      canal: campanha.canal,
      filtros: campanha.filtros || {},
    }
    await campanhasStore.criarCampanha(dados)
    Swal.fire('Duplicada!', 'Nova campanha criada como rascunho.', 'success')
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao duplicar campanha.'
      Swal.fire('Erro!', msg, 'error')
    }
  }
}

function renderDetalhesHtml(data) {
  const progresso = data.total_destinatarios > 0
    ? Math.round(((data.total_enviados + data.total_falhas) / data.total_destinatarios) * 100)
    : 0
  const statusLabels = {
    rascunho: 'Rascunho',
    agendado: 'Agendado',
    em_andamento: 'Em andamento',
    concluido: 'Concluído',
    erro: 'Erro',
    cancelado: 'Cancelado',
  }

  return `
    <div style="text-align: left;">
      <p><strong>Canal:</strong> ${data.canal === 'email' ? 'Email' : 'WhatsApp'}</p>
      <p><strong>Template:</strong> ${data.template?.nome || '-'}</p>
      <p><strong>Status:</strong> ${statusLabels[data.status] || data.status}</p>
      <hr>
      <p><strong>Total de destinatários:</strong> ${data.total_destinatarios}</p>
      <p><strong>Enviados com sucesso:</strong> <span style="color: green; font-weight: bold;">${data.total_enviados}</span></p>
      <p><strong>Falhas:</strong> <span style="color: red; font-weight: bold;">${data.total_falhas}</span></p>
      <div style="margin-top: 15px;">
        <div style="background: #e0e0e0; border-radius: 10px; height: 24px; overflow: hidden; position: relative;">
          <div style="background: ${data.status === 'erro' ? '#f44336' : '#4caf50'}; height: 100%; width: ${progresso}%; transition: width 0.5s ease;"></div>
          <span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 12px; font-weight: bold; color: ${progresso > 50 ? 'white' : '#333'};">${progresso}%</span>
        </div>
      </div>
      ${data.status === 'em_andamento' ? '<p style="text-align: center; font-size: 11px; color: #888; margin-top: 8px;">Atualizando a cada 3 segundos...</p>' : ''}
    </div>
  `
}

async function handleDetalhesCampanha(campanha) {
  let detalhesPolling = null

  try {
    const data = await campanhasStore.carregarCampanha(campanha.id)

    Swal.fire({
      title: data.nome,
      html: renderDetalhesHtml(data),
      width: 500,
      confirmButtonText: 'Fechar',
      didOpen: () => {
        // Se em andamento, iniciar polling dentro do modal
        if (data.status === 'em_andamento') {
          detalhesPolling = setInterval(async () => {
            try {
              const { data: statusData } = await axios.get(`/api/notificacoes/campanhas/${campanha.id}/status`)
              const swalContent = Swal.getHtmlContainer()
              if (swalContent) {
                swalContent.innerHTML = renderDetalhesHtml({ ...data, ...statusData })
              }
              if (['concluido', 'erro', 'cancelado'].includes(statusData.status)) {
                clearInterval(detalhesPolling)
                detalhesPolling = null
              }
            } catch {
              // silencioso
            }
          }, 3000)
        }
      },
      willClose: () => {
        if (detalhesPolling) {
          clearInterval(detalhesPolling)
          detalhesPolling = null
        }
      },
    })
  } catch (err) {
    if (!err._handled) {
      Swal.fire('Erro!', 'Não foi possível carregar os detalhes.', 'error')
    }
  }
}

async function handleContarDestinatarios(form, callback) {
  try {
    const { data } = await axios.post('/api/notificacoes/campanhas/contar-destinatarios', {
      canal: form.canal,
      filtros: form.filtros,
    })
    callback(data.total)
  } catch (err) {
    console.error('Erro ao contar destinatários:', err)
    callback(0)
  }
}

// === LOGS ===
async function handleCarregarLogs(filtros = {}, pagina = 1) {
  try {
    await logsStore.carregarLogs(filtros, pagina)
  } catch (err) {
    if (!err._handled) {
      console.error('Erro ao carregar logs:', err)
    }
  }
}

async function handleCarregarStats() {
  try {
    await logsStore.carregarStats()
  } catch (err) {
    if (!err._handled) {
      console.error('Erro ao carregar estatísticas:', err)
    }
  }
}

// === LEMBRETES ===
async function handleCarregarLembretes() {
  try {
    await lembretesStore.carregarLembretes()
  } catch (err) {
    if (!err._handled) {
      console.error('Erro ao carregar lembretes:', err)
    }
  }
}

async function handleSalvarLembrete(dados) {
  try {
    if (lembretesStore.modoEdicao.value) {
      await lembretesStore.atualizarLembrete(lembretesStore.lembreteSelecionado.value.id, dados)
      Swal.fire('Sucesso!', 'Lembrete atualizado com sucesso.', 'success')
    } else {
      await lembretesStore.criarLembrete(dados)
      Swal.fire('Sucesso!', 'Lembrete criado com sucesso.', 'success')
    }
    lembretesStore.fecharModal()
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao salvar lembrete.'
      Swal.fire('Erro!', msg, 'error')
    }
  }
}

async function handleExcluirLembrete(lembrete) {
  const result = await Swal.fire({
    title: 'Excluir lembrete?',
    text: `O lembrete "${lembrete.nome}" será excluído permanentemente.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Sim, excluir',
    cancelButtonText: 'Cancelar',
  })

  if (!result.isConfirmed) return

  try {
    await lembretesStore.excluirLembrete(lembrete.id)
    Swal.fire('Excluído!', 'Lembrete excluído com sucesso.', 'success')
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao excluir lembrete.'
      Swal.fire('Erro!', msg, 'error')
    }
  }
}

async function handleToggleLembrete(lembrete) {
  try {
    await lembretesStore.toggleAtivo(lembrete.id)
    const status = lembrete.ativo ? 'desativado' : 'ativado'
    Swal.fire({
      icon: 'success',
      title: `Lembrete ${status}!`,
      timer: 1500,
      showConfirmButton: false,
    })
  } catch (err) {
    if (!err._handled) {
      const msg = err.response?.data?.message || 'Erro ao alterar status do lembrete.'
      Swal.fire('Erro!', msg, 'error')
    }
  }
}

async function handleLogsLembrete(lembrete) {
  try {
    const logs = await lembretesStore.carregarLogs(lembrete.id)

    if (!logs || logs.length === 0) {
      Swal.fire({
        title: `Logs: ${lembrete.nome}`,
        html: '<p style="color: #888;">Nenhum envio registrado ainda.</p>',
        confirmButtonText: 'Fechar',
      })
      return
    }

    const logsHtml = logs.slice(0, 20).map(log => {
      const statusIcon = log.status === 'enviado' ? '✅' : log.status === 'falha' ? '❌' : '⏳'
      const dataEnvio = new Date(log.created_at).toLocaleString('pt-BR')
      return `<div style="display: flex; align-items: center; gap: 8px; padding: 6px 0; border-bottom: 1px solid #eee;">
        <span>${statusIcon}</span>
        <span style="flex: 1;">${log.paciente?.primeiro_nome || 'Paciente'}</span>
        <span style="color: #888; font-size: 12px;">${dataEnvio}</span>
      </div>`
    }).join('')

    Swal.fire({
      title: `Logs: ${lembrete.nome}`,
      html: `<div style="max-height: 400px; overflow-y: auto; text-align: left;">${logsHtml}</div>`,
      width: 500,
      confirmButtonText: 'Fechar',
    })
  } catch (err) {
    if (!err._handled) {
      Swal.fire('Erro!', 'Não foi possível carregar os logs.', 'error')
    }
  }
}

async function handleContarDestinatariosLembrete(form, callback) {
  try {
    const total = await lembretesStore.contarDestinatarios({
      tipo: form.tipo,
      canal: form.canal,
      filtros: form.filtros,
    })
    callback(total)
  } catch (err) {
    console.error('Erro ao contar destinatários:', err)
    callback(0)
  }
}

// === INIT ===
onMounted(async () => {
  await templatesStore.carregarVariaveis()
  await handleCarregarTemplates()
  await carregarWebhookSettings()

  // Carregar campanhas e iniciar polling se houver alguma em andamento
  await handleCarregarCampanhas()
  verificarCampanhasEmAndamento()

  // Carregar lembretes
  await handleCarregarLembretes()
})

// Verifica se há campanhas em andamento e inicia polling automaticamente
function verificarCampanhasEmAndamento() {
  const campanhaAtiva = campanhasStore.campanhas.value.find(c => c.status === 'em_andamento')
  if (campanhaAtiva) {
    iniciarPolling(campanhaAtiva.id)
  }
}

onBeforeUnmount(() => {
  pararPolling()
})
</script>

<style scoped>
.notificacoes-container {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 25px;
}

.page-header h1 {
  margin: 0 0 8px 0;
  font-size: 28px;
  color: #333;
}

.page-description {
  margin: 0;
  color: #666;
  font-size: 15px;
}

/* Tabs */
.tabs-container {
  display: flex;
  gap: 5px;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 0;
}

.tab-btn {
  padding: 12px 24px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  margin-bottom: -2px;
}

.tab-btn:hover {
  color: #333;
  background: #f5f5f5;
}

.tab-btn.active {
  color: #4caf50;
  border-bottom-color: #4caf50;
}

.tab-btn i {
  font-size: 16px;
}

/* Tab content */
.tab-content {
  background: white;
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 10px 10px;
  min-height: 500px;
}

/* === ABA INSTRUÇÕES === */
.tab-instrucoes {
  padding: 30px;
}

.instrucoes-content h2 {
  margin: 0 0 10px 0;
  font-size: 22px;
  color: #333;
}

.instrucoes-intro {
  font-size: 15px;
  color: #555;
  margin-bottom: 25px;
  line-height: 1.6;
}

.instrucoes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.instrucao-card {
  background: #f8faf8;
  border: 1px solid #e0e8e0;
  border-radius: 10px;
  padding: 20px;
  position: relative;
}

.instrucao-numero {
  position: absolute;
  top: -12px;
  left: 16px;
  background: #4caf50;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
}

.instrucao-card h3 {
  margin: 8px 0 10px 0;
  font-size: 16px;
  color: #333;
}

.instrucao-card p {
  margin: 0;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.instrucao-card code {
  background: #e8f5e9;
  color: #2e7d32;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}

.instrucoes-section {
  margin-bottom: 25px;
}

.instrucoes-section h3 {
  margin: 0 0 12px 0;
  font-size: 17px;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.instrucoes-section p {
  font-size: 14px;
  color: #555;
  line-height: 1.6;
}

.tipos-envio-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 15px;
}

.tipo-envio-item {
  display: flex;
  flex-direction: column;
  padding: 15px;
  background: #f8faf8;
  border: 1px solid #e0e8e0;
  border-radius: 8px;
  text-align: center;
}

.tipo-envio-item .tipo-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.tipo-envio-item strong {
  font-size: 13px;
  color: #333;
  margin-bottom: 4px;
}

.tipo-envio-item p {
  margin: 0;
  font-size: 11px;
  color: #666;
  line-height: 1.4;
}

.variaveis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 8px;
}

.variavel-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 6px;
  font-size: 13px;
}

.variavel-item code {
  background: #e8f5e9;
  color: #2e7d32;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.variavel-item span {
  color: #666;
}

/* Fluxo visual */
.fluxo-visual {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-top: 10px;
}

.fluxo-item {
  background: #fff;
  border: 2px solid #4caf50;
  color: #333;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
}

.fluxo-item.destaque-email {
  border-color: #1976d2;
  color: #1976d2;
}

.fluxo-item.destaque-whatsapp {
  border-color: #25d366;
  color: #128c7e;
}

.fluxo-seta {
  font-size: 20px;
  color: #999;
  font-weight: bold;
}

.fluxo-ou {
  font-size: 12px;
  color: #999;
  font-style: italic;
}

/* === ABA N8N WEBHOOKS === */
.tab-webhooks {
  padding: 30px;
}

.webhooks-content h2 {
  margin: 0 0 8px 0;
  font-size: 22px;
  color: #333;
}

.webhooks-header p {
  color: #666;
  font-size: 14px;
  margin: 0 0 25px 0;
}

.webhooks-form {
  background: #f8faf8;
  border: 1px solid #e0e8e0;
  border-radius: 10px;
  padding: 25px;
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #4caf50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.15);
}

.form-hint {
  display: block;
  font-size: 12px;
  color: #888;
  margin-top: 4px;
}

.input-with-toggle {
  position: relative;
  display: flex;
}

.input-with-toggle .form-input {
  padding-right: 44px;
}

.btn-toggle-token {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  padding: 4px;
}

.token-field {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.token-actions {
  display: flex;
  gap: 8px;
}

.btn-gerar-token,
.btn-copiar-token {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-gerar-token {
  background: #ff9800;
  color: white;
}

.btn-gerar-token:hover {
  background: #f57c00;
}

.btn-copiar-token {
  background: #607d8b;
  color: white;
}

.btn-copiar-token:hover:not(:disabled) {
  background: #546e7a;
}

.btn-copiar-token:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.webhooks-actions {
  display: flex;
  gap: 12px;
  margin-top: 5px;
}

.btn-salvar-webhook,
.btn-testar-webhook {
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
}

.btn-salvar-webhook {
  background: #4caf50;
  color: white;
}

.btn-salvar-webhook:hover:not(:disabled) {
  background: #43a047;
}

.btn-testar-webhook {
  background: #1976d2;
  color: white;
}

.btn-testar-webhook:hover:not(:disabled) {
  background: #1565c0;
}

.btn-salvar-webhook:disabled,
.btn-testar-webhook:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.webhook-test-result {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 15px;
  font-size: 14px;
}

.webhook-test-result.success {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}

.webhook-test-result.error {
  background: #fce4ec;
  color: #c62828;
  border: 1px solid #ef9a9a;
}

.test-main {
  display: flex;
  align-items: center;
  gap: 10px;
}

.test-icon {
  font-size: 18px;
}

.test-detalhes {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 13px;
  font-weight: 600;
}

.canal-ok {
  color: #2e7d32;
}

.canal-fail {
  color: #c62828;
}

/* Webhooks info */
.webhooks-info h3 {
  margin: 0 0 10px 0;
  font-size: 17px;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.webhooks-info p {
  font-size: 14px;
  color: #555;
  margin-bottom: 15px;
}

.workflow-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 25px;
}

.workflow-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: #f5f5f5;
  border-radius: 8px;
}

.workflow-icon {
  font-size: 28px;
}

.workflow-item strong {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 2px;
}

.workflow-item p {
  margin: 0;
  font-size: 12px;
  color: #777;
}

.credenciais-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.credencial-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #f5f5f5;
  border-radius: 6px;
  font-size: 13px;
  color: #555;
}

.credencial-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  color: white;
  white-space: nowrap;
}

.credencial-badge.email {
  background: #1976d2;
}

.credencial-badge.whatsapp {
  background: #25d366;
}

.credencial-badge.ia {
  background: #7c4dff;
}

/* Dark mode */
.dark-mode .page-header h1 {
  color: #f1f1f1;
}

.dark-mode .page-description {
  color: #aaa;
}

.dark-mode .tabs-container {
  border-color: #444;
}

.dark-mode .tab-btn {
  color: #aaa;
}

.dark-mode .tab-btn:hover {
  color: #f1f1f1;
  background: #2a2a2a;
}

.dark-mode .tab-btn.active {
  color: #4caf50;
}

.dark-mode .tab-content {
  background: #1e1e1e;
  border-color: #444;
}

/* Dark mode - Instruções */
.dark-mode .instrucoes-content h2,
.dark-mode .instrucao-card h3,
.dark-mode .instrucoes-section h3 {
  color: #f1f1f1;
}

.dark-mode .instrucoes-intro,
.dark-mode .instrucao-card p,
.dark-mode .instrucoes-section p {
  color: #bbb;
}

.dark-mode .instrucao-card {
  background: #252525;
  border-color: #3a3a3a;
}

.dark-mode .instrucao-card code {
  background: #1b4332;
  color: #81c784;
}

.dark-mode .instrucoes-section h3 {
  border-bottom-color: #444;
}

.dark-mode .variavel-item {
  background: #2a2a2a;
}

.dark-mode .variavel-item code {
  background: #1b4332;
  color: #81c784;
}

.dark-mode .variavel-item span {
  color: #aaa;
}

.dark-mode .fluxo-visual {
  background: #252525;
}

.dark-mode .fluxo-item {
  background: #1e1e1e;
  color: #e0e0e0;
}

.dark-mode .tipo-envio-item {
  background: #252525;
  border-color: #3a3a3a;
}

.dark-mode .tipo-envio-item strong {
  color: #e0e0e0;
}

.dark-mode .tipo-envio-item p {
  color: #888;
}

/* Dark mode - Webhooks */
.dark-mode .webhooks-content h2,
.dark-mode .webhooks-info h3 {
  color: #f1f1f1;
}

.dark-mode .webhooks-header p,
.dark-mode .webhooks-info p {
  color: #aaa;
}

.dark-mode .webhooks-form {
  background: #252525;
  border-color: #3a3a3a;
}

.dark-mode .form-group label {
  color: #e0e0e0;
}

.dark-mode .form-input {
  background: #1e1e1e;
  border-color: #444;
  color: #e0e0e0;
}

.dark-mode .form-input:focus {
  border-color: #4caf50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.25);
}

.dark-mode .form-hint {
  color: #777;
}

.dark-mode .webhooks-info h3 {
  border-bottom-color: #444;
}

.dark-mode .workflow-item {
  background: #2a2a2a;
}

.dark-mode .workflow-item strong {
  color: #e0e0e0;
}

.dark-mode .workflow-item p {
  color: #888;
}

.dark-mode .credencial-item {
  background: #2a2a2a;
  color: #bbb;
}

.dark-mode .webhook-test-result.success {
  background: #1b4332;
  color: #81c784;
  border-color: #2e7d32;
}

.dark-mode .webhook-test-result.error {
  background: #4a1a1a;
  color: #ef9a9a;
  border-color: #c62828;
}

@media (max-width: 768px) {
  .notificacoes-container {
    padding: 15px;
  }

  .page-header h1 {
    font-size: 22px;
  }

  .tabs-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .tab-btn {
    padding: 10px 16px;
    font-size: 14px;
    white-space: nowrap;
  }

  .tab-instrucoes,
  .tab-webhooks {
    padding: 20px;
  }

  .instrucoes-grid {
    grid-template-columns: 1fr;
  }

  .variaveis-grid {
    grid-template-columns: 1fr;
  }

  .fluxo-visual {
    flex-direction: column;
  }

  .webhooks-actions {
    flex-direction: column;
  }
}
</style>
